/**
 * Types + fetchery dla pluginu `c_crm_project_management` rozszerzajacego shell
 * `c_user_accounts` (projekty w ktorych uczestniczy zalogowany user).
 *
 * Backend (IRESTAPI, routing po NAZWIE METODY - `IAPI_` strip):
 *   - GET /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userProjects
 *   - GET /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userServices
 *   - GET /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userWorkLog
 *
 * Zakres dostepu liczy backend (`IMODEL_M_CRM_PROJECTS::getUserProjects`): user
 * widzi projekt gdy jest jego wlascicielem, klientem albo czlonkiem zespolu.
 * Uslugi sa scope'owane TAK SAMO - przez uczestnictwo w projekcie do ktorego
 * usluga jest przypieta (`IMODEL_M_SERVICES__SERVICES::getUserServices`).
 *
 * Obie zakladki sa bramkowane dostepnoscia modulu po stronie backendu
 * (`sharingContentForApi`) - gdy modulu nie ma, akcja nie trafia do `extension.actions`,
 * wiec front nigdy nie wola endpointu ktory i tak by rzucil bledem.
 *
 * Lista jest client-side gridem (TanStack Table, ten sam wzorzec co faktury) -
 * backend zwraca caly bounded zbior, a sort/filtr/wyszukiwarka/paginacja dzieja
 * sie w react-cms.
 */

export type UserProject = {
  id: number;
  /** Numer projektu w formacie `P/{id}/{rok}`. */
  nr: string;
  name: string;
  description: string;
  /** Adres wdrozenia (kolumna `domain_online`), pusty string gdy nie ustawiony. */
  domain_online: string;
  /** Data utworzenia (Y-m-d H:i:s). */
  add_date: string;
  /** Daty planu (Y-m-d) - null gdy nie ustawione. */
  start_date: string | null;
  end_date: string | null;
  deadline_date: string | null;
  /** Numer etapu (slownik `crm_project_stages`). */
  stage: number;
  /** Etykieta etapu ze slownika; null gdy slownik nie zawiera pozycji. */
  stage_name: string | null;
  /** Rola usera w zespole; null gdy dostep jako wlasciciel albo klient. */
  role_name: string | null;
  /**
   * Menadzer projektu z kontaktem - osoba prowadzaca projekt po naszej stronie.
   * null gdy projekt nie ma przydzielonego menadzera.
   */
  project_manager: { fullname: string; email: string | null; telephone: string | null } | null;
  /** Czy user jest klientem tego projektu. */
  is_client: boolean;
  ended: boolean;
  received: boolean;
  /** Powod wstrzymania projektu; null gdy projekt nie jest wstrzymany. */
  stop_reason: string | null;
};

export type UserProjectsResponse = {
  ok: true;
  data: {
    projects: UserProject[];
    /** `true` gdy lista zostala obcieta do limitu serwera (300). */
    truncated: boolean;
  };
};

/**
 * Pobiera liste projektow usera z route handler proxy.
 *
 * @returns `{ projects, truncated }`
 * @throws Error gdy fetch fail / response !ok (caller obsluguje przez try/catch).
 */
export async function fetchUserProjects(): Promise<{
  projects: UserProject[];
  truncated: boolean;
}> {
  const res = await fetch("/api/iiapi/c-crm-project-management/projects", {
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`Fetch projects failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as
    | UserProjectsResponse
    | { ok: false; error?: string; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error(
      "message" in body ? body.message ?? "Fetch failed" : "Invalid response shape",
    );
  }
  return {
    projects: body.data?.projects ?? [],
    truncated: body.data?.truncated ?? false,
  };
}

/**
 * Dodatkowe pole usługi widoczne dla klienta.
 *
 * Definicję (nazwa, typ, wymagalność, widoczność) ustala PAKIET w panelu admina,
 * a wartość wpisuje operator przy konkretnej usłudze - np. pakiet SLA definiuje
 * pole "Liczba godzin obsługi w miesiącu", a usługa danego klienta ma w nim 10.
 * Backend zwraca WYŁĄCZNIE pola oznaczone jako widoczne w panelu klienta i tylko
 * te z uzupełnioną wartością, w kolejności z definicji.
 */
export type UserServiceAdditionalField = {
  name: string;
  value: string;
};

export type UserService = {
  id: number;
  /** Numer uslugi w formacie `S/{id}`. */
  nr: string;
  id_project: number;
  /** Nazwa projektu do ktorego usluga jest przypieta. */
  project_name: string | null;
  type: number;
  /** Nazwa typu uslugi ze slownika modulu Uslugi; null gdy typ usuniety. */
  type_name: string | null;
  package: number;
  package_name: string | null;
  /**
   * Dodatkowe pola usługi widoczne dla klienta (pary nazwa/wartość).
   * Pusta tablica = pakiet nie definiuje takich pól albo nie mają uzupełnionych wartości.
   */
  additional_fields: UserServiceAdditionalField[];
  id_status: number;
  status_name: string | null;
  description: string | null;
  /** Data uruchomienia (Y-m-d H:i:s). */
  add_date: string;
  /** Data wygasniecia (Y-m-d H:i:s); null dla uslug bezterminowych. */
  expiry_date: string | null;
  /**
   * Czy usluga jest BIEZACA - status w zbiorze {nowa, w trakcie dostarczania,
   * do aktywacji, aktywna, zablokowana}. To domyslny widok listy, identycznie jak
   * w gridzie modulu Uslugi w panelu admina (`getActiveServiceStatuses`).
   * Pozostale statusy = uslugi nieaktywne (historia).
   * NIE to samo co `id_status === 4` - zablokowana tez jest biezaca.
   */
  is_current: boolean;
  /**
   * Czy usluga pochodzi z pakietu oznaczonego jako PRIORYTETOWY - ta sama flaga,
   * ktora pozwala klientowi nadac priorytet zgloszeniu.
   */
  is_priority: boolean;
  /**
   * Czy pakiet W OGOLE dopuszcza przedluzanie. Rozne od `renew_href`, ktore bywa
   * puste takze gdy przedluzenie jest mozliwe, ale okno sie nie otworzylo.
   */
  renew_allowed: boolean;
  /**
   * Czy usluga przedluzy sie AUTOMATYCZNIE - pakiet ma wlaczony automat, a usluga
   * ma czym go zrealizowac (zgoda na fakture, cena, okres, termin). Ten sam warunek,
   * ktory sprawdza przebieg CRON wykonujacy przedluzenie.
   */
  auto_renew: boolean;
  /**
   * Dni do wygasniecia; wartosc UJEMNA = tyle dni PO terminie.
   * null dla uslug bezterminowych (brak `expiry_date`) - to co innego niz 0.
   */
  expiry_days: number | null;
  /** Czy `expiry_date` juz minela (niezaleznie od statusu). */
  is_expired: boolean;
  /**
   * Ostatni dzien okresu karencji (Y-m-d) - po nim usluga jest ostatecznie wygasla
   * i podlega usunieciu. null dla uslug bezterminowych.
   */
  grace_until: string | null;
  /** Czy okres karencji juz minal (usluga do usuniecia). */
  is_after_grace: boolean;
  /** Czy zlozono rezygnacje z uslugi. */
  resignation: boolean;
  /** Data rezygnacji (Y-m-d); null gdy nie zlozono. */
  resignation_date: string | null;
  /**
   * Absolutny URL panelu przedluzenia w domenie backendu
   * (`M_SERVICES_MCONTROLLER::getRenewPanelUrl` - id uslugi zaszyfrowane w adresie,
   * klient nie musi sie logowac). null gdy przedluzenie nie jest teraz mozliwe
   * wg dat i konfiguracji pakietu.
   */
  renew_href: string | null;
  /**
   * Dostep do panelu serwera dla uslug hostingowych - null dla pozostalych uslug oraz
   * dla hostingowych, ktorym processing nie zalozyl jeszcze konta (powstaje przy
   * aktywacji) albo je skasowal. HASLA TU NIE MA: klient dostal je jednorazowo mailem,
   * a zgubione ustawia sam pod `password_reset_url`.
   */
  hosting: ServiceHostingAccess | null;
  /**
   * Stan monitorowania strony - null dla uslug spoza monitoringu oraz dla tych, ktorych
   * przebieg jeszcze nie sprawdzal. HISTORII zdarzen tu nie ma: lista potrafi miec setki
   * pozycji, wiec dociaga ja `fetchServiceMonitoring` po wejsciu w zakladke.
   */
  monitoring: ServiceMonitoringState | null;
};

/** Stan monitorowania strony uslugi (zapisany przez processing, odczyt bez odpytywania strony). */
export type ServiceMonitoringState = {
  /** Monitorowany adres. */
  url: string;
  /** Czy strona odpowiada. */
  is_up: boolean;
  /** Od kiedy trwa biezacy stan (Y-m-d H:i:s). */
  since: string;
  /** Kiedy sprawdzano ostatni raz (Y-m-d H:i:s). */
  last_check: string;
  /** Kod odpowiedzi HTTP z ostatniego sprawdzenia. */
  http_code: number | null;
  /** Czas odpowiedzi w sekundach. */
  response_time: number | null;
  /** Dni do konca waznosci certyfikatu (ujemne = wygasl); null dla adresu bez https. */
  ssl_days: number | null;
  /** Data waznosci certyfikatu (Y-m-d H:i:s); pusty string gdy nieznana. */
  ssl_valid_to: string;
  /** Dostepnosc w oknie `uptime_days` [%]. */
  uptime_percent: number;
  /** Dlugosc okna, z ktorego liczona jest dostepnosc [dni]. */
  uptime_days: number;
};

/** Zdarzenie monitoringu - moment zmiany, a nie pojedyncze sprawdzenie. */
export type ServiceMonitoringEvent = {
  /** Kiedy (Y-m-d H:i:s). */
  date: string;
  /** Rodzaj: 1=niedostepna 2=powrot 3=certyfikat konczy sie 4=certyfikat wygasl. */
  type: number;
  /** Nazwa rodzaju w jezyku panelu. */
  type_name: string;
  /** Opis zdarzenia. */
  message: string;
  /** Przy powrocie: ile trwala niedostepnosc [s]. */
  duration_seconds: number | null;
};

/** Odpowiedz endpointu monitoringu uslugi. */
export type ServiceMonitoring = {
  state: ServiceMonitoringState | null;
  events: ServiceMonitoringEvent[];
};

/** Dane dostepowe do panelu serwera hostingowego (ISPConfig). */
export type ServiceHostingAccess = {
  /** Adres panelu, pod ktory kierujemy klienta. */
  panel_url: string;
  /** Formularz odzyskiwania hasla w panelu serwera. */
  password_reset_url: string;
  /** Login klienta w panelu serwera. */
  login: string;
  /** Czy konto jest zablokowane (usluga po terminie) - panel wpuszcza, ale uslugi nie dzialaja. */
  locked: boolean;
  /** Kiedy konto powstalo na serwerze (Y-m-d H:i:s); pusty string gdy nieznane. */
  created_at: string;
};

export type UserServicesResponse = {
  ok: true;
  data: {
    services: UserService[];
    /** `true` gdy lista zostala obcieta do limitu serwera (500). */
    truncated: boolean;
    /** Adres publicznej strony zamawiania uslugi (katalog `c_crm_project_management`
        w trybie `service_order`). NULL gdy domena nie ma takiej podstrony - wtedy
        przycisk "Zamow usluge" sie nie pojawia. */
    order_href: string | null;
  };
};

/**
 * Pobiera liste uslug z projektow usera z route handler proxy.
 *
 * @returns `{ services, truncated }`
 * @throws Error gdy fetch fail / response !ok (caller obsluguje przez try/catch).
 */
export async function fetchUserServices(returnUrl?: string): Promise<{
  services: UserService[];
  truncated: boolean;
  orderHref: string | null;
}> {
  /* `returnUrl` - adres, na ktory panel przedluzenia odesle klienta po zakonczeniu.
     Podaje go przegladarka, bo backend obsluguje wiele domen CMS i nie wie, z ktorej
     klient przyszedl. Trafia do zaszyfrowanego payloadu linku po stronie PHP. */
  const query = returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : "";
  const res = await fetch(`/api/iiapi/c-crm-project-management/services${query}`, {
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`Fetch services failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as
    | UserServicesResponse
    | { ok: false; error?: string; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error(
      "message" in body ? body.message ?? "Fetch failed" : "Invalid response shape",
    );
  }
  return {
    services: body.data?.services ?? [],
    truncated: body.data?.truncated ?? false,
    orderHref: body.data?.order_href ?? null,
  };
}

/* -------------------------------------------------------------------------
 * Regulamin uslugi
 *
 * Backend (IRESTAPI):
 *   - GET /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userServiceRegulamin/{id}
 *
 * INNY dokument niz regulamin panelu klienta (`fetchPanelRegulamin`): tamten bramkuje
 * wejscie do panelu i pochodzi z konfiguracji katalogu, ten opisuje warunki KONKRETNEJ
 * uslugi i pochodzi z modulu Uslugi (wpis dla typu uslugi, ewentualnie zawezony
 * do pakietu).
 *
 * Wglad, nie akceptacja - zgode klient wyraza w panelu przedluzenia po stronie backendu.
 * ------------------------------------------------------------------------- */

export type ServiceRegulamin = {
  service: { id: number; nr: string };
  /**
   * Czy dla tej uslugi w ogole ustalono regulamin. `false` = operator nie dodal
   * dokumentu dla tego typu/pakietu - front mowi to wprost, zamiast pokazywac
   * pusta zakladke.
   */
  has_regulamin: boolean;
  /** Tresc obowiazujacego dokumentu (HTML, sanityzowany na backendzie). */
  text: string;
  /** Publiczny podglad obowiazujacego dokumentu (`/IPUBLIC/...`); null gdy brak. */
  href: string | null;
  /** Czy klient zaakceptowal regulamin tej uslugi (flaga zapisana przy usludze). */
  accepted: boolean;
  /**
   * Data akceptacji (Y-m-d H:i:s); null takze dla zgod sprzed wprowadzenia metryki -
   * wtedy `accepted` jest true, ale daty nie znamy.
   */
  accept_date: string | null;
  /** Publiczny podglad ZAAKCEPTOWANEJ wersji; null gdy nie zapisano ktorej dotyczyla. */
  accepted_href: string | null;
  /** Czy zaakceptowany dokument to INNY dokument niz obowiazujacy dzisiaj. */
  accepted_outdated: boolean;
};

/**
 * Pobiera regulamin obowiazujacy dla wskazanej uslugi.
 *
 * @throws TaskApiError z kodem Imperiala (2260042 = brak uslugi lub dostepu).
 */
export async function fetchServiceMonitoring(idService: number): Promise<ServiceMonitoring> {
  const res = await fetch(
    `/api/iiapi/c-crm-project-management/service-monitoring?id_service=${encodeURIComponent(String(idService))}`,
    { cache: "no-store" },
  );
  return readTaskJson<ServiceMonitoring>(res, "Nie udało się pobrać danych monitoringu.");
}

export async function fetchServiceRegulamin(idService: number): Promise<ServiceRegulamin> {
  const res = await fetch(
    `/api/iiapi/c-crm-project-management/service-regulamin?id_service=${encodeURIComponent(String(idService))}`,
    { cache: "no-store" },
  );
  return readTaskJson<ServiceRegulamin>(res, "Nie udało się pobrać regulaminu usługi.");
}

/* -------------------------------------------------------------------------
 * Zadania projektu
 *
 * Backend (IRESTAPI):
 *   - GET  /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userProjectTasks/{id}
 *   - GET  /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userTaskFormOptions/{id}
 *   - POST /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userTaskCreate
 *
 * Lista obejmuje wszystkie zadania projektu niezależnie od autora, także prace
 * wewnętrzne zespołu - warunkiem jest dostęp do projektu. Zawężanie do własnych
 * (`is_own`) albo przypisanych (`is_assigned_to_me`) robi filtr po stronie frontu.
 * ------------------------------------------------------------------------- */

/** Typy zadania dostepne dla klienta (stale `M_CRM_TASKS_TYPE_*` z define.php). */
export const TASK_TYPE_TASK = 1;
export const TASK_TYPE_SUPPORT = 2;
export const TASK_TYPE_WARRANTY = 6;

export type UserTask = {
  id: number;
  /** Numer zadania w formacie `T/{id}`. */
  nr: string;
  name: string;
  description: string;
  id_type: number;
  /** Etykieta typu ze slownika modulu Zadania; null gdy typ nieznany. */
  type_name: string | null;
  id_status: number;
  status_name: string | null;
  id_priority: number;
  /** Etykieta priorytetu ze slownika modulu Zadania; null gdy priorytet nieznany. */
  priority_name: string | null;
  /** ID osoby prowadzacej zadanie po stronie zespolu; null gdy nieprzydzielone. */
  assigned_to: number | null;
  assigned_to_fullname: string | null;
  /** Postep realizacji 0-100. */
  percent_complete: number;
  /** Data zgloszenia (Y-m-d H:i:s). */
  add_date: string;
  start_date: string | null;
  end_date: string | null;
  /** Usluga ktorej dotyczy zgloszenie (typ Wsparcie); null dla pozostalych typow. */
  id_project_service: number | null;
  /** Czy zadanie zgłosił zalogowany user - filtr "Tylko moje zgłoszenia". */
  is_own: boolean;
  /** Czy zadanie prowadzi zalogowany user (`assigned_to`) - filtr "Przypisane do mnie". */
  is_assigned_to_me: boolean;
  /** Zgłosił / obserwuje / dodał wpis - osobisty udział w zadaniu. */
  is_participant: boolean;
  /** Imię i nazwisko autora zgłoszenia; null gdy konta już nie ma. */
  owner_fullname: string | null;
  ended: boolean;
};

/**
 * Powod niedostepnosci typu zadania - backend zwraca go zamiast ukrywac opcje,
 * zeby klient wiedzial CZEGO brakuje, a nie tylko ze "nie ma takiego wyboru".
 *
 *  - `requires_package`        - typ Zadanie ograniczony w konfiguracji do wybranych
 *                                pakietow, a projekt nie ma aktywnej uslugi z zadnego,
 *  - `no_active_service`       - typ Wsparcie wymaga co najmniej jednej aktywnej uslugi,
 *  - `no_support_service`      - brak aktywnej uslugi z pakietu wsparcia,
 *  - `no_warranty_service`     - brak aktywnej uslugi z pakietu gwarancyjnego,
 *  - `warranty_not_configured` - operator nie wskazal pakietow gwarancyjnych w konfiguracji,
 *  - `no_services_module`      - modul Uslugi niedostepny w instalacji.
 */
export type TaskTypeReason =
  | "requires_package"
  | "no_active_service"
  | "no_support_service"
  | "no_warranty_service"
  | "warranty_not_configured"
  | "no_services_module";

export type TaskTypeOption = {
  id: number;
  name: string;
  available: boolean;
  /** null gdy typ dostepny. */
  reason: TaskTypeReason | null;
  /** Czy wybor typu wymusza wskazanie uslugi (`id_project_service`). */
  requires_service: boolean;
  /**
   * Uslugi dopuszczone DLA TEGO TYPU. Zbiory roznia sie miedzy typami, bo
   * konfiguracja CRM wskazuje osobne pakiety dla zadania, wsparcia i gwarancji -
   * przy gwarancji klient wybiera wylacznie z uslug gwarancyjnych. Pusta tablica
   * gdy typ uslugi nie wymaga albo jest niedostepny.
   */
  services: TaskFormService[];
};

export type TaskFormService = {
  id: number;
  /** Numer uslugi w formacie `S/{id}`. */
  nr: string;
  type_name: string | null;
  package_name: string | null;
  expiry_date: string | null;
};

export type TaskFormOptions = {
  project: { id: number; nr: string; name: string };
  /** Menadzer do ktorego trafi zgloszenie; null = projekt bez PM. */
  project_manager: { id: number; fullname: string } | null;
  /** Twarda bramka formularza - bez menadzera nie ma komu przydzielic zadania. */
  can_add: boolean;
  blocked_reason: "no_project_manager" | null;
  /** Typy zgloszenia; lista dopuszczonych uslug siedzi w `types[].services`. */
  types: TaskTypeOption[];
  /**
   * Czy klient moze sam nadac priorytet zgloszeniu. Backend zwraca `true` tylko gdy
   * projekt ma AKTYWNA usluge z pakietu oznaczonego jako priorytetowy.
   */
  can_set_priority: boolean;
  /** Slownik priorytetow do wyboru; pusty gdy `can_set_priority` jest false. */
  priorities: { id: number; name: string }[];
  /** Adres formularza zapytania ofertowego z konfiguracji; null gdy nieustawiony. */
  offer_request_url: string | null;
};

export type CreateTaskPayload = {
  id_project: number;
  id_type: number;
  name: string;
  description?: string;
  /** Priorytet - wysylany tylko gdy formularz go pokazal (`can_set_priority`). */
  id_priority?: number;
  id_project_service?: number;
  /** Zalaczniki zgloszenia - opcjonalne. */
  files?: File[];
};

/** Blad zapisu niosacy kod Imperiala - formularz mapuje go na komunikat przy polu. */
export class TaskApiError extends Error {
  readonly code: number;

  constructor(message: string, code: number) {
    super(message);
    this.name = "TaskApiError";
    this.code = code;
  }
}

/**
 * Wspolne odczytanie odpowiedzi proxy - rzuca `TaskApiError` z kodem Imperiala,
 * zeby formularz mogl przypiac komunikat do konkretnego pola zamiast pokazywac
 * ogolny "cos poszlo nie tak".
 */
async function readTaskJson<T>(res: Response, fallbackMessage: string): Promise<T> {
  const body = (await res.json().catch(() => null)) as
    | { ok: true; data: T }
    | { ok: false; error?: string; message?: string; code?: number }
    | null;

  if (!body || !("ok" in body)) {
    throw new TaskApiError(fallbackMessage, 0);
  }
  if (body.ok !== true) {
    throw new TaskApiError(body.message ?? fallbackMessage, body.code ?? 0);
  }
  return body.data;
}

/**
 * Pobiera zadania projektu widoczne dla zalogowanego usera.
 *
 * @throws TaskApiError gdy response !ok (caller obsluguje przez try/catch).
 */
export async function fetchProjectTasks(idProject: number): Promise<{
  /** Dane projektu do naglowka - front wchodzacy z linku zna tylko ID z adresu. */
  project: { id: number; nr: string; name: string };
  tasks: UserTask[];
  truncated: boolean;
}> {
  const res = await fetch(
    `/api/iiapi/c-crm-project-management/tasks?id_project=${encodeURIComponent(String(idProject))}`,
    { cache: "no-store" },
  );
  const data = await readTaskJson<{
    project: { id: number; nr: string; name: string };
    tasks: UserTask[];
    truncated: boolean;
  }>(res, "Nie udało się pobrać zadań projektu.");
  return {
    project: data?.project ?? { id: idProject, nr: "", name: "" },
    tasks: data?.tasks ?? [],
    truncated: data?.truncated ?? false,
  };
}

/**
 * Pobiera opcje formularza zgloszenia zadania.
 *
 * UWAGA: wolanie ma efekt uboczny po stronie backendu - projekt bez menadzera
 * generuje pilne powiadomienie do administracji. Wolaj przy otwarciu formularza,
 * nie przy renderze listy.
 *
 * @throws TaskApiError gdy response !ok.
 */
export async function fetchTaskFormOptions(idProject: number): Promise<TaskFormOptions> {
  const res = await fetch(
    `/api/iiapi/c-crm-project-management/task-options?id_project=${encodeURIComponent(String(idProject))}`,
    { cache: "no-store" },
  );
  return readTaskJson<TaskFormOptions>(res, "Nie udało się pobrać opcji formularza.");
}

/** Zalacznik zadania albo wpisu historii. */
export type TaskFile = {
  name: string;
  ext: string;
  /** Rozmiar w MB (string z 3 miejscami); null gdy pliku fizycznie brak. */
  size: string | null;
  add_date: string;
  description: string | null;
  /** Absolutny, zaszyfrowany adres pobrania (`/IGETFILE/...` w domenie backendu). */
  href: string | null;
  is_image: boolean;
};

/**
 * Pojedynczy wpis historii zadania.
 *
 * `available: false` = wpis ZAMKNIETY przepływem informacji w zespole projektu
 * (rola autora oznaczona `show_info_only_from_flow_roles`, a odbiorca nie ma jej
 * w swoim workflow). Backend zwraca wtedy wylacznie date - bez autora, tresci
 * i zalacznikow. Front pokazuje taka pozycje jako zaslepke, zeby oś czasu
 * pozostala prawdziwa: klient widzi, ze praca trwa, nie poznajac szczegolow.
 */
export type TaskHistoryEntry = {
  id: number;
  /** Numer porzadkowy wpisu liczony od najstarszego (jak w panelu admina). */
  index: number;
  add_date: string;
  available: boolean;
  author: string | null;
  /** Log zmian pol - HTML (moduł otacza wartosci `<strong>`), sanityzowany na backendzie. */
  changes: string | null;
  /** Tresc wpisu - HTML z edytora panelu. */
  text: string | null;
  files: TaskFile[];
};

export type TaskChild = {
  id: number;
  nr: string;
  name: string;
  status_name: string;
  percent_complete: number;
};

export type TaskDetail = {
  task: {
    id: number;
    nr: string;
    name: string;
    /** HTML z edytora panelu. */
    description: string | null;
    id_type: number;
    type_name: string | null;
    id_status: number;
    status_name: string | null;
    id_priority: number;
    priority_name: string | null;
    percent_complete: number;
    /**
     * Czas SPRZEDANY klientowi (godziny), a nie faktyczny czas pracy zespolu -
     * to dwie rozne liczby. null gdy nic jeszcze nie rozliczono.
     */
    time_worked_client: number | null | undefined;
    add_date: string;
    last_modify_date: string;
    start_date: string | null;
    end_date: string | null;
    owner_fullname: string | null;
    assigned_to_fullname: string | null;
    project: { id: number; name: string } | null;
    id_project_service: number | null;
    ended: boolean;
    to_acceptance: boolean;
    files: TaskFile[];
    child_tasks: TaskChild[];
  };
  /** Menadzer projektu - przejmie zadanie po odpowiedzi; null gdy nieprzydzielony. */
  project_manager: { id: number; fullname: string } | null;
  /** Czy klient moze teraz odpowiedziec / zamknac zadanie. */
  can_update: boolean;
  /** Powod blokady formularza aktualizacji; null gdy dostepny. */
  update_blocked_reason: "no_project_manager" | "task_closed" | null;
  history: TaskHistoryEntry[];
  /** Ile wpisow jest zamknietych przeplywem informacji (takze tych ukrytych). */
  history_locked_count: number;
  /** Czy konfiguracja CRM kaze CALKOWICIE ukrywac zamkniete wpisy. */
  history_hidden: boolean;
};

/**
 * Pobiera szczegoly zadania wraz z pelna historia wpisow.
 *
 * @throws TaskApiError gdy response !ok (2260030 = brak zadania lub dostepu).
 */
export async function fetchTaskDetail(idTask: number): Promise<TaskDetail> {
  const res = await fetch(
    `/api/iiapi/c-crm-project-management/task-detail?id_task=${encodeURIComponent(String(idTask))}`,
    { cache: "no-store" },
  );
  return readTaskJson<TaskDetail>(res, "Nie udało się pobrać szczegółów zadania.");
}

/** Operacje, ktore klient moze wykonac na zadaniu z panelu konta. */
export type TaskUpdateAction = "reply" | "complete";

/**
 * Odpowiada w zadaniu albo uznaje je za zrealizowane.
 *
 * `reply` wymaga tresci i przekazuje zadanie z powrotem menadzerowi projektu.
 * `complete` zamyka zadanie; tresc jest opcjonalnym komentarzem.
 *
 * Obie operacje sa zablokowane, gdy projekt nie ma menadzera (kod 2260027).
 *
 * @throws TaskApiError z kodem Imperiala (2260027, 2260030, 2260032-2260034).
 */
export async function updateProjectTask(payload: {
  id_task: number;
  action: TaskUpdateAction;
  text?: string;
  /** Zalaczniki odpowiedzi - opcjonalne, jak przy zgloszeniu zadania. */
  files?: File[];
}): Promise<{ task: { id: number; nr: string; id_status: number; status_name: string | null } }> {
  /* `multipart/form-data` - odpowiedz moze niesc zalaczniki, ktore backend odbiera
     z `$_FILES['files']`. Content-Type ustawia przegladarka (boundary). */
  const form = new FormData();
  form.append("id_task", String(payload.id_task));
  form.append("action", payload.action);
  if (payload.text) form.append("text", payload.text);
  for (const file of payload.files ?? []) form.append("files[]", file, file.name);

  const res = await fetch("/api/iiapi/c-crm-project-management/task-update", {
    method: "POST",
    body: form,
    cache: "no-store",
  });
  return readTaskJson<{
    task: { id: number; nr: string; id_status: number; status_name: string | null };
  }>(res, "Nie udało się zaktualizować zadania.");
}

/**
 * Zglasza nowe zadanie w projekcie.
 *
 * @throws TaskApiError z kodem Imperiala (2260024-2260029 = walidacja zgloszenia).
 */
export async function createProjectTask(
  payload: CreateTaskPayload,
): Promise<{ task: { id: number; nr: string } }> {
  /* `multipart/form-data`, nie JSON - zgloszenie moze niesc zalaczniki, a backend
     odbiera je z `$_FILES['files']`. Content-Type NIE jest ustawiany recznie:
     przegladarka sama dokleja boundary, bez ktorego serwer nie rozbierze body. */
  const form = new FormData();
  form.append("id_project", String(payload.id_project));
  form.append("id_type", String(payload.id_type));
  form.append("name", payload.name);
  if (payload.description) form.append("description", payload.description);
  if (payload.id_priority !== undefined)
    form.append("id_priority", String(payload.id_priority));
  if (payload.id_project_service !== undefined)
    form.append("id_project_service", String(payload.id_project_service));

  //Nazwa pola z nawiasami - PHP sklada z tego tablice w `$_FILES['files']`.
  for (const file of payload.files ?? []) form.append("files[]", file, file.name);

  const res = await fetch("/api/iiapi/c-crm-project-management/tasks", {
    method: "POST",
    body: form,
    cache: "no-store",
  });
  return readTaskJson<{ task: { id: number; nr: string } }>(
    res,
    "Nie udało się zgłosić zadania.",
  );
}

/* -------------------------------------------------------------------------
 * Regulamin panelu klienta
 *
 * Tresc regulaminu wpisuje operator w konfiguracji katalogu komponentu, a checkbox
 * "wymagaj akceptacji" bramkuje WEJSCIE do panelu: dopoki klient nie zaakceptuje,
 * nie zobaczy projektow, zadan ani uslug.
 *
 * `accepted` liczy backend wobec KONKRETNEJ wersji tresci (sha256), wiec zmiana
 * regulaminu automatycznie odnawia pytanie o zgode.
 * ------------------------------------------------------------------------- */

export type PanelRegulamin = {
  /** Czy akceptacja jest wymagana do wejscia (false takze przy pustej tresci). */
  required: boolean;
  /** Czy uzytkownik zaakceptowal TE wersje tresci. */
  accepted: boolean;
  /** Tresc regulaminu (HTML, sanityzowany na backendzie). */
  text: string;
  /** Data akceptacji (Y-m-d H:i:s); null gdy brak zgody. */
  accept_date: string | null;
};

/**
 * Pobiera regulamin panelu i stan jego akceptacji.
 *
 * @throws TaskApiError gdy response !ok.
 */
export async function fetchPanelRegulamin(): Promise<PanelRegulamin> {
  const res = await fetch("/api/iiapi/c-crm-project-management/regulamin", {
    cache: "no-store",
  });
  return readTaskJson<PanelRegulamin>(res, "Nie udało się pobrać regulaminu panelu.");
}

/**
 * Zapisuje akceptacje regulaminu przez zalogowanego uzytkownika.
 *
 * Backend zapisuje date, adres IP i skrot tresci - front nie przekazuje niczego,
 * zeby nie dalo sie zaakceptowac innej wersji niz ta faktycznie obowiazujaca.
 *
 * @throws TaskApiError z kodem Imperiala (2260040 = panel bez regulaminu).
 */
export async function acceptPanelRegulamin(): Promise<{ accepted: boolean; accept_date: string }> {
  const res = await fetch("/api/iiapi/c-crm-project-management/regulamin", {
    method: "POST",
    cache: "no-store",
  });
  return readTaskJson<{ accepted: boolean; accept_date: string }>(
    res,
    "Nie udało się zapisać akceptacji regulaminu.",
  );
}

/* -------------------------------------------------------------------------
 * Dziennik pracy
 *
 * Backend (IRESTAPI):
 *   - GET /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userWorkLog
 *
 * Liste wpisow widzi WYLACZNIE kontrahent projektu. Projekty, w ktorych user jest czlonkiem
 * zespolu albo wlascicielem, wracaja w `restricted_projects` z zamaskowanym adresem
 * konta kontrahenta i samym podsumowaniem godzin - front pokazuje sumy oraz informacje,
 * na ktore konto sie zalogowac, zeby zobaczyc wpisy.
 * Backend pokazuje prace z dnia D od 12:00 najblizszego dnia roboczego po D (kalendarz
 * dni wolnych), ukrywa wpisy z zerowym czasem sprzedanym oraz zwraca wylacznie czas
 * sprzedany klientowi.
 * ------------------------------------------------------------------------- */

/** Pojedynczy wpis dziennika pracy. */
export type WorkLogEntry = {
  id: number;
  /** Dzien pracy (Y-m-d). */
  work_date: string;
  project_id: number;
  /** Numer projektu w formacie `P/{id}/{rok}`. */
  project_nr: string;
  project_name: string;
  task_id: number | null;
  task_name: string | null;
  /** Pracownik zamaskowany: imie i pierwsza litera nazwiska (`Jan K***`); null gdy brak imienia. */
  employee: string | null;
  /** Rodzaj pracy ze slownika aktywnosci; null gdy brak etykiety. */
  activity_name: string | null;
  text: string;
  /** Czas sprzedany klientowi w godzinach. */
  hours: number;
  /** `false` = godziny przepracowane, ale nierozliczane z klientem. */
  billed: boolean;
  /** Powod nierozliczenia (tylko przy `billed=false`); null gdy nie podano. */
  not_billed_reason: string | null;
};

/** Suma godzin nierozliczanych dla jednego powodu. */
export type WorkLogNotBilledReason = {
  /** Powod; null = wpisy bez podanego powodu. */
  reason: string | null;
  hours: number;
  positions: number;
};

/** Sumy godzin dziennika w okresie (liczone przez backend po calym zakresie). */
export type WorkLogSummary = {
  billed_hours: number;
  not_billed_hours: number;
  total_hours: number;
  not_billed_reasons: WorkLogNotBilledReason[];
};

/** Projekt, w ktorym user nie jest kontrahentem - dane dziennika dostepne na innym koncie. */
export type WorkLogRestrictedProject = {
  id: number;
  nr: string;
  name: string;
  /**
   * Zamaskowany adres e-mail konta kontrahenta, a gdy konto nie ma adresu - zamaskowany
   * login. null gdy projekt nie ma kontrahenta.
   */
  client_account_masked: string | null;
  /** Sumy godzin projektu w okresie - bez listy wpisow (ta zostaje na koncie kontrahenta). */
  summary: WorkLogSummary;
};

export type WorkLog = {
  /** Zakres faktycznie uzyty przez backend (Y-m-d) - domyslnie biezacy miesiac. */
  date_from: string;
  date_to: string;
  /** Ostatni dzien, z ktorego wpisy sa juz widoczne (liczony z kalendarza dni roboczych). */
  visible_until: string;
  /** Projekty kontrahenta - opcje filtra. */
  projects: Array<{ id: number; nr: string; name: string }>;
  restricted_projects: WorkLogRestrictedProject[];
  entries: WorkLogEntry[];
  /** Sumy po CALYM zakresie, niezaleznie od obciecia listy. */
  summary: WorkLogSummary;
  /** `true` gdy lista wpisow zostala obcieta do limitu serwera (1000). */
  truncated: boolean;
};

/**
 * Pobiera dziennik pracy zalogowanego kontrahenta.
 *
 * @param filter Zakres dat (Y-m-d) i opcjonalny projekt; brak dat = biezacy miesiac.
 * @throws TaskApiError z kodem Imperiala (2260023 = projekt niedostepny, 2260062 = zla data).
 */
export async function fetchWorkLog(filter: {
  dateFrom?: string;
  dateTo?: string;
  projectId?: number | null;
}): Promise<WorkLog> {
  const query = new URLSearchParams();
  if (filter.dateFrom) query.set("date_from", filter.dateFrom);
  if (filter.dateTo) query.set("date_to", filter.dateTo);
  if (filter.projectId) query.set("id_project", String(filter.projectId));

  const res = await fetch(
    `/api/iiapi/c-crm-project-management/worklog${query.toString() ? `?${query.toString()}` : ""}`,
    { cache: "no-store" },
  );
  return readTaskJson<WorkLog>(res, "Nie udało się pobrać dziennika pracy.");
}

/* -------------------------------------------------------------------------
 * Cennik godzin
 *
 * Backend (IRESTAPI):
 *   - GET /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/userPriceList
 *
 * Stawki netto z cennika aktywności przypisane do zalogowanego konta - te same,
 * po których rozliczany jest dziennik pracy. Tylko konta kontrahentów (bramka
 * po stronie backendu), tylko odczyt.
 * ------------------------------------------------------------------------- */

export type PriceListItem = {
  activity_id: number;
  /** Nazwa aktywności ze słownika w języku frontu. */
  activity_name: string;
  /** Stawka netto za godzinę; null gdy w cenniku nie ustawiono kwoty. */
  hour_price: number | null;
};

export type PriceList = {
  /** Waluta faktur z konfiguracji księgowości (kod ISO, np. PLN). */
  currency: string;
  /** Pozycje cennika posortowane po nazwie aktywności. */
  items: PriceListItem[];
};

/**
 * Pobiera cennik godzin zalogowanego kontrahenta.
 *
 * @throws TaskApiError z kodem Imperiala (2260067 = konto spoza grup kontrahentów).
 */
export async function fetchPriceList(): Promise<PriceList> {
  const res = await fetch("/api/iiapi/c-crm-project-management/pricelist", { cache: "no-store" });
  return readTaskJson<PriceList>(res, "Nie udało się pobrać cennika.");
}
